import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'

/**
 * QueryClient compartilhado entre todos os hooks TanStack Query.
 *
 * Defaults (atualizados pós-Sprint 5 — o valor canônico é o do código abaixo):
 *  - staleTime: 45s → navegação de volta a uma tela renderiza o cache na hora
 *    e só refetcha quando o dado pode ter mudado (era 0 na migração A03 pra
 *    preservar comportamento; subiu pra 45s depois de validado)
 *  - gcTime: 5min → padrão da lib
 *  - refetchOnWindowFocus: false → evita refetch automático em focus
 *    (cada hook decide se quer ligar)
 *  - retry: 1 → 1 retry em failure (Supabase às vezes flaky em mobile)
 *
 * ── Observabilidade (2026-05-27) ────────────────────────────────────────────
 * QueryCache.onError + MutationCache.onError centralizam a captura de TODO erro
 * de query/mutation no Sentry. Antes, cada mutation só fazia `toast.error(...)`
 * no seu próprio onError — o erro real (RLS negada, violação de constraint,
 * exceção inesperada) morria no toast e NUNCA chegava ao Sentry. Eram ~40
 * mutations e dezenas de queries cegas. Agora um único ponto captura todas.
 *
 * Filtro: erros puramente de conexão (offline) não são capturados — não são
 * acionáveis e só gerariam ruído. O resto (incluindo "Failed to fetch" com o
 * usuário online, que indica CORS/CSP/edge down) vai pro Sentry.
 */

/** Usuário offline: erro de conexão do lado dele, não é bug nosso → não captura. */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

function keyToString(key: unknown): string | undefined {
  if (Array.isArray(key) && key.length > 0 && typeof key[0] === 'string') return key[0]
  return undefined
}

function captureQueryError(
  error: unknown,
  kind: 'query' | 'mutation',
  key: unknown,
  extra?: Record<string, unknown>,
): void {
  if (isOffline()) return
  Sentry.withScope(scope => {
    scope.setTag('source', `react-query.${kind}`)
    const k = keyToString(key)
    if (k) scope.setTag('query_key', k)
    scope.setContext('react_query', { kind, key, ...(extra || {}) })
    if (error instanceof Error) {
      Sentry.captureException(error)
    } else {
      Sentry.captureException(new Error(typeof error === 'string' ? error : JSON.stringify(error)))
    }
  })
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => captureQueryError(error, 'query', query.queryKey),
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, _context, mutation) =>
      captureQueryError(error, 'mutation', mutation.options.mutationKey, {
        has_variables: variables !== undefined,
      }),
  }),
  defaultOptions: {
    queries: {
      // 45s: navegação de volta a uma tela renderiza cache na hora (com isPending
      // nos hooks, sem skeleton) e refaz fetch só quando o dado pode ter mudado.
      staleTime: 45_000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
})
